import { useState } from 'react';
import { useQuery } from '@apollo/client';
import { PROJECT_FILES_QUERY } from '../../graphql/queries';
import { LINKED_FILE_GROUPS, LinkedFile } from '../../utils/linkedFiles';
import styles from './NoteFilePicker.module.css';

interface NoteFilePickerProps {
  projectId: string;
  excludeIds: string[];
  onPick: (file: LinkedFile) => void;
  onClose: () => void;
  busy?: boolean;
}

// "Link a project file to this note": one dropdown grouped by section.
const NoteFilePicker = ({ projectId, excludeIds, onPick, onClose, busy }: NoteFilePickerProps) => {
  const [chosen, setChosen] = useState('');
  const { data, loading } = useQuery(PROJECT_FILES_QUERY, {
    variables: { projectId, page: 1, limit: 500 },
    fetchPolicy: 'cache-and-network',
  });
  const files: LinkedFile[] = (data?.projectFiles?.files ?? []).filter((f: LinkedFile) => !excludeIds.includes(f.id));
  const groups = LINKED_FILE_GROUPS
    .map((g) => ({ ...g, files: files.filter((f) => f.type === g.type) }))
    .filter((g) => g.files.length > 0);
  const chosenFile = files.find((f) => f.id === chosen);

  return (
    <div className={styles.picker} onClick={(e) => e.stopPropagation()}>
      <label className={styles.label} htmlFor={`note-file-picker-${projectId}`}>Link a project file</label>
      <select
        id={`note-file-picker-${projectId}`}
        className={styles.select}
        value={chosen}
        onChange={(e) => setChosen(e.target.value)}
        disabled={loading && files.length === 0}
      >
        <option value="">{loading && files.length === 0 ? 'Loading files…' : files.length === 0 ? 'No other files in this project' : '— Choose a file —'}</option>
        {groups.map((g) => (
          <optgroup key={g.type} label={g.label}>
            {g.files.map((f) => (
              <option key={f.id} value={f.id}>{f.originalName}{f.status !== 'ready' ? ` (${f.status})` : ''}</option>
            ))}
          </optgroup>
        ))}
      </select>
      <div className={styles.row}>
        <button type="button" className={styles.ghost} onClick={onClose}>Cancel</button>
        <div style={{ flex: 1 }} />
        <button
          type="button"
          className={styles.primary}
          disabled={!chosenFile || busy}
          onClick={() => chosenFile && onPick(chosenFile)}
        >
          {busy ? 'Linking…' : 'Link'}
        </button>
      </div>
    </div>
  );
};

export default NoteFilePicker;
